import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { Cms, ICarDetails, ILeasingUsedCarIntegratedValuesRequest, LoanResponse } from '@impact/data';
import { OverlayService } from '@impactdk/ngx-overlay';
import { Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { CheckoutService, UsedCarCheckoutFinancingTabsOrder } from '../../checkout/checkout.service';
import { SettingsService, SiteIds } from '../../core/settings.service';
import { ILeasingConfiguratorRanges } from '../../leasing/configurator/leasing-configurator.component';
import {
    IActiveLeasingView,
    LEASING_PLAN_TYPES,
    LEASING_PURPOSE_TYPES,
    LeasingService,
} from '../../leasing/leasing.service';
import { CheckoutFlowComponent } from '../../pages/checkout/checkout-flow.component';
import { TabGroupComponent } from '../../tabs/tab-group.component';
import { UsedCarFinancingTypes } from '../used-car.models';
import { UsedCarService } from '../used-car.service';
import { checkmarkIcon, helpCircleIcon, leasingLogoIcon } from '../../../icons';
import { IRadioGroupItem } from '../../form-elements/radio-toggle/radio-toggle.component';
import { mergeObjects } from '../../core/helpers';
import { FilterService } from '../../filter-service/filter.service';
@Component({
    selector: 'app-used-car-financing-motorpoint',
    template: `
        <h3 class="used-car-details__header">
            {{ 'used_car_details.menu_financing' | translate }}
        </h3>
        <ng-container *ngIf="carData && pageData">
            <ng-container *ngIf="site === siteIds.MOTORPOINT; else notLeasable">
                <app-rich-text [html]="pageData.financingHeader"></app-rich-text>

                    <div class="used-car-financing-options">
                        <div class="pdp-toggel-heading-section">
                             <div class="toggle-container">
                                 <!-- LEASING -->
                                <button
                                    class="pdp-toggle-button"
                                    [ngClass]="{ 'selected': selectedOption === 'leasing' }"
                                    (click)="onToggleSelect('leasing', usedCarFinancingTypes.Leasing)">
                                    {{ 'used_car_details.leasing_toggels_label' | translate }}
                                </button>
                                <!-- CASH -->
                                <button
                                    class="pdp-toggle-button"
                                    [ngClass]="{ 'selected': selectedOption === 'cash' }"
                                    (click)="onToggleSelect('cash', usedCarFinancingTypes.Cash)">
                                    {{ 'used_car_details.cash_toggels_label' | translate }}
                                </button>
                             </div>
                        </div>
                        <div class="used-car-financing__options-content">
                            <ng-container *ngTemplateOutlet="LeasingFinance"></ng-container>
                            <ng-container *ngTemplateOutlet="CashFinance"></ng-container>
                        </div>
                    </div>
            </ng-container>
        </ng-container>

        <!-- TEMPLATES   -->

        <ng-template #CashFinance>
            <div class="used-car-financing__options-content-item" [ngClass]="{'active-option': selectedCheckoutMethod === usedCarFinancingTypes.Cash}">
                <div class="used-car-financing__motorpoint-leasing tab-group__content-wrapper">
                    <div class="used-car-financing__tab-columns">
                        <div class="used-car-financing__column">
                            <app-rich-text [html]="pageData.cashOptionDetailsText"></app-rich-text>
                        </div>
                        <div class="used-car-financing__column leasing-box">
                            <div class="used-car-financing__details-header lease-now">
                                <div class="pdp-cash-details-box">
                                    <div class="pdp-toggels-cash-price">{{ carData.price?.data | defaultcurrency }}</div>
                                    <div class="pdp-toggels-cash-price-description">{{'used_car_details.toggels_cash_price_description_text' | translate }}</div>
                                </div>
                                <button class="used-car-financing__buy-button button--secondary"
                                    [disabled]="leasingConfigDataLoading || !leasingPriceIsValid"
                                    (click)="goToCheckout()">
                                    {{ 'leasing.lease_vehicle_cta_buttont_text' | translate }}
                                </button>
                                <div class="leasing-logo">${leasingLogoIcon}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ng-template>
        <!-- LEASING  -->
        <ng-template #LeasingFinance>
            <div class="used-car-financing__options-content-item" [ngClass]="{'active-option': selectedCheckoutMethod === usedCarFinancingTypes.Leasing}">
            <div class="used-car-financing__motorpoint-leasing tab-group__content-wrapper">
            <div [ngClass]="{'data-loading': leasingDataLoading}">
                <div class="used-car-financing__tab-columns" [ngSwitch]="isReady">
                    <app-component-loader *ngSwitchCase="false"></app-component-loader>

                    <ng-container *ngSwitchCase="true">
                        <div class="used-car-financing__column">
                            <app-leasing-configurator class="animate-fade-in"
                                [downPaymentRange]="leasingConfiguratorRangesData.downPaymentRange"
                                [leasingMonthsRange]="leasingConfiguratorRangesData.leasingMonthsRange"
                                [yearlyDistanceInKmRange]="leasingConfiguratorRangesData.yearlyDistanceInKmRange"

                                [downPayment]="leasingConfiguratorData.downPayment"
                                [monthlyTerms]="leasingConfiguratorData.monthlyTerms"
                                [yearlyDistanceInKm]="leasingConfiguratorData.yearlyDistanceInKm"

                                [showResidualValue]="false"
                                [leasingTexts]="leasingDataTexts"
                                [isDisabled]="leasingDataLoading || !!(!leasingPriceIsValid)"

                                [leasingTypes]="leasingTypes$ | async"
                                [leasingPurposes]="leasingPurposes$ | async"

                                (handleChange)="updateConfiguratorInputData($event)">
                            </app-leasing-configurator>

                            <div class="used-car-financing__actions">
                                <button class="used-car-financing__buy-button used-car-financing__buy-button--secondary button--primary mp-hidden"
                                    [disabled]="leasingConfigDataLoading"
                                    (click)="goToCheckout()">
                                    {{ 'leasing.lease_vehicle_cta_buttont_text' | translate }}
                                </button>
                            </div>
                        </div>

                        <div class="used-car-financing__column leasing-box">
                            <div class="used-car-financing__details-header lease-now" [ngSwitch]="!!leasingPriceIsValid">
                                <app-dynamic-value [isLoading]="leasingConfigDataLoading" class="no-price-text" *ngSwitchCase="false">
                                    <div class="used-car-financing__details-header-text">{{'generic.call_for_price' | translate }}</div>
                                    <div class="maintain-newlines">{{'generic.call_for_price_description' | translate }}</div>
                                </app-dynamic-value>

                                <div class="used-car-financing__details-header-text" *ngSwitchCase="true">
                                    <app-dynamic-value [isLoading]="leasingConfigDataLoading" *ngIf="leasingConfiguratorData?.monthlyPayment; else callForPrice">
                                            {{ leasingConfiguratorData.monthlyPayment | number: '1.0-0' }}  {{ leasingDataTexts?.general?.costSuffixText }}
                                    </app-dynamic-value>
                                    <ng-template #callForPrice>
                                        <div class="used-car-financing__details-header-text">{{'generic.call_for_price' | translate }}</div>
                                        <div class="maintain-newlines motorpoint-call-for-price">{{'generic.call_for_price_description' | translate }}</div>
                                    </ng-template>

                                    <div class="secondary-price" *ngIf="leasingConfiguratorData?.monthlyPayment">
                                        <button class="button--as-link">
                                            <span *ngIf="leasingConfiguratorData.leasingtypeForPriceCalculation === leasingServiceTypes.PRIVATE">
                                                {{ 'leasing.leasing_service_vat_private' | translate }}
                                            </span>
                                            <span *ngIf="leasingConfiguratorData.leasingtypeForPriceCalculation === leasingServiceTypes.BUSINESS">
                                                {{ 'leasing.leasing_service_vat_business' | translate }}
                                            </span>
                                            <div class="button--mini">i</div>
                                        </button>
                                    </div>
                                </div>

                                <button class="used-car-financing__buy-button button--secondary"
                                    [disabled]="leasingConfigDataLoading || !leasingPriceIsValid"
                                    (click)="goToCheckout()">
                                    {{ 'leasing.lease_vehicle_cta_buttont_text' | translate }}
                                </button>

                                <div class="leasing-logo">${leasingLogoIcon}</div>
                            </div>

                            <div class="used-car-financing__details-header">
                                <div class="used-car-financing-loan-details__title heading text-uppercase">{{ 'leasing.leasing_heading' | translate }}</div>
                            </div>

                            <div class="leasing-plan__disclaimer">{{ 'leasing.leasing_service_description' | translate }}</div>

                            <div class="used-car-financing__details-summary">
                                <div class="used-car-financing-loan-details__title">{{ 'generic.details_text' | translate }}</div>

                                <ul class="used-car-financing__details-list">
                                    <li role="row" class="used-car-financing__details-list-row">
                                        <div role="cell" class="used-car-financing__details-list-cell">
                                            {{leasingDataTexts?.general?.monthlyPaymentText}}
                                        </div>
                                        <div role="cell" class="used-car-financing__details-list-cell" [ngSwitch]="leasingPriceIsValid">
                                            <app-dynamic-value [isLoading]="leasingConfigDataLoading">
                                                <ng-container *ngSwitchCase="true">{{ leasingConfiguratorData.monthlyPayment | defaultcurrency }}</ng-container>
                                                <span class="bold" *ngSwitchCase="false">{{'generic.call_for_price' | translate }}</span>
                                            </app-dynamic-value>
                                        </div>
                                    </li>
                                    <li role="row" class="used-car-financing__details-list-row">
                                        <div role="cell" class="used-car-financing__details-list-cell">
                                            {{leasingDataTexts?.leasingPlanStep?.firstTimePaymentText}}
                                        </div>
                                        <div role="cell" class="used-car-financing__details-list-cell" [ngSwitch]="leasingPriceIsValid">
                                            <app-dynamic-value [isLoading]="leasingConfigDataLoading">
                                                <ng-container *ngSwitchCase="true">{{ leasingConfiguratorData.downPayment | defaultcurrency }}</ng-container>
                                                <ng-container *ngSwitchCase="false">&mdash;</ng-container>
                                            </app-dynamic-value>
                                        </div>
                                    </li>
                                    <li role="row" class="used-car-financing__details-list-row">
                                        <div role="cell" class="used-car-financing__details-list-cell">
                                            {{leasingDataTexts?.leasingPlanStep?.durationOfLoanText}}
                                        </div>
                                        <div role="cell" class="used-car-financing__details-list-cell" [ngSwitch]="leasingPriceIsValid">
                                            <app-dynamic-value [isLoading]="leasingConfigDataLoading">
                                                <ng-container *ngSwitchCase="true">{{ leasingConfiguratorData.monthlyTerms }} måneder</ng-container>
                                                <ng-container *ngSwitchCase="false">&mdash;</ng-container>
                                            </app-dynamic-value>
                                        </div>
                                    </li>
                                    <li role="row" class="used-car-financing__details-list-row">
                                        <div role="cell" class="used-car-financing__details-list-cell">
                                            {{leasingDataTexts?.general?.kmPerYearText}}
                                        </div>
                                        <div role="cell" class="used-car-financing__details-list-cell" [ngSwitch]="leasingPriceIsValid">
                                            <app-dynamic-value [isLoading]="leasingConfigDataLoading">
                                                <ng-container *ngSwitchCase="true">{{ leasingConfiguratorData.yearlyDistanceInKm | number: '1.0-0' }}</ng-container>
                                                <ng-container *ngSwitchCase="false">&mdash;</ng-container>
                                            </app-dynamic-value>
                                        </div>
                                    </li>
                                    <li role="row" class="used-car-financing__details-list-row" *ngIf="leasingConfiguratorData.leasingType === LeasingPlanTypes.TYPE_FINANCIAL">
                                        <div role="cell" class="used-car-financing__details-list-cell">
                                            {{ 'leasing.leasing_scrap_value' | translate }}
                                        </div>
                                        <div role="cell" class="used-car-financing__details-list-cell" [ngSwitch]="scrapValue !== undefined">
                                            <app-dynamic-value [isLoading]="leasingConfigDataLoading">
                                                <ng-container *ngSwitchCase="true">{{ scrapValue | defaultcurrency }}</ng-container>
                                                <ng-container *ngSwitchCase="false">&mdash;</ng-container>
                                            </app-dynamic-value>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </ng-container>
                </div>

                <div class="leasing-plan__disclaimer used-car-financing__disclaimer" *ngIf="isReady">
                    {{ leasingDataTexts?.general?.leasingFooterDisclaimerText }}
                </div>
            </div>
                </div>
            </div>
        </ng-template>

        <ng-template #notLeasable>
            <app-rich-text class="used-car-financing__not-buyable-text" [html]="pageData?.notForSaleText"></app-rich-text>
        </ng-template>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsedCarFinancingMotorpointComponent implements OnInit, OnDestroy {
    @HostBinding('class') class = 'used-car-financing';
    @ViewChild('tabs') tabs: TabGroupComponent;
    @Input() carData: ICarDetails;
    @Input() leasingDataTexts: any;
    @Input() pageData: any;

    usedCarFinancingTabsOrder = UsedCarCheckoutFinancingTabsOrder;
    usedCarFinancingTypes = UsedCarFinancingTypes;

    pageData$: Observable<Cms.UsedCarDetailsPage | undefined>;
    disclaimerText$: Observable<string>;
    car$: Observable<ICarDetails>;

    site: string;
    siteIds = SiteIds;
    leasingServiceTypes = LEASING_PURPOSE_TYPES;
    LeasingPlanTypes = LEASING_PLAN_TYPES;

    leasingTotalCost: number;
    leasingDataLoading = false;
    selectedCheckoutMethod: UsedCarFinancingTypes;
    leasingPriceIsValid: boolean;

    leasingTypes$: Observable<IRadioGroupItem[]>;
    leasingPurposes$: Observable<IRadioGroupItem[]>;

    private unsubscribe = new Subject<void>();

    leasingConfiguratorData: any;
    leasingConfiguratorRangesData: ILeasingConfiguratorRanges;
    isInitial = true;

    // UI variables
    leasingConfigDataLoading: boolean;
    leasingConfigDataReady: boolean;
    isReady: boolean;
    scrapValue: number;

    showCashPrice: boolean;
    selectedOption: string = 'leasing';

    private staticRequestData: ILeasingUsedCarIntegratedValuesRequest;

    private cancel$: Subject<void> = new Subject<void>();


    constructor(
        private cdr: ChangeDetectorRef,
        private checkoutService: CheckoutService,
        private leasingService: LeasingService,
        public filterService: FilterService,
        private overlayService: OverlayService,
        private usedCarService: UsedCarService,
        settingsService: SettingsService
    ) {
        this.leasingConfigDataLoading = true;
        this.leasingConfigDataReady = false;
        this.isReady = false;

        this.car$ = this.usedCarService.carDetails$;

        this.showCashPrice = this.filterService.getShowCashPrice();

        settingsService.get().pipe(takeUntil(this.unsubscribe)).subscribe((settings) => {
            this.site = settings.siteNameId;
        });

        this.leasingTypes$ = settingsService.get().pipe(map((settings) => settings.leasingCalculator.leasingTypes.map((type: { label: string, key: string }) => ({
            label: type.label,
            value: type.key,
        }))));

        this.leasingPurposes$ = settingsService.get().pipe(map((settings) => settings.leasingCalculator.leasingPurposes.map((type: { label: string, key: string }) => ({
            label: type.label,
            value: type.key,
        }))));
    }

    ngOnInit() {
        this.leasingConfigDataLoading = true;
        this.leasingConfigDataReady = false;

        this.staticRequestData = {
            carMilage: this.carData?.mileage?.data,
            downpayment: undefined,
            durationInMonths: undefined,
            firstRegistrationDate: String(this.carData.registrationDate?.data),
            fueltype: this.carData?.propellant?.data === 'Benzin' ? 'B' : this.carData?.propellant?.data === 'El' ? 'E' : 'D',
            km: undefined,
            isActiveMotorPoint: true,
            make: this.carData?.make?.value,
            model: this.carData?.model?.value,
            modelDescription: this.carData?.variant?.value,
            registrationFee: this.carData?.registrationFee?.data,
            steelPrice: this.carData?.steelPrice?.data,
            vehicleType: 'P',
        };

        // Setting up data objects
        this.leasingConfiguratorRangesData = {
            downPaymentRange: {
                minValue: 0,
                maxValue: 100
            },
            leasingMonthsRange: [],
            yearlyDistanceInKmRange: {
                minValue: 0,
                maxValue: 100
            },
            residualValueRange: {
                minValue: 0,
                maxValue: 100
            },
        };

        this.leasingConfiguratorData = {
            downPayment: 0,
            monthlyTerms: 12,
            yearlyDistanceInKm: 15000
        }

        this.leasingService.getActiveLeasingToggles().pipe(take(1)).subscribe((leasingToggles: IActiveLeasingView | undefined) => {
            if (leasingToggles) {
                this.leasingConfiguratorData.leasingType = leasingToggles.leasingType;
                this.leasingConfiguratorData.leasingtypeForPriceCalculation = leasingToggles.leasingPurpose;
            } else {
                this.leasingConfiguratorData.leasingType = LEASING_PLAN_TYPES.TYPE_FINANCIAL;
                this.leasingConfiguratorData.leasingtypeForPriceCalculation = LEASING_PLAN_TYPES.PURPOSE_BUSINESS;
            }

            this.getVehicleLeasingData();
        });

    }

    ngAfterViewInit(): void {
        /**
         * Sets the checkout method based on the selected financing type.
         * Subscribes to the financing type observable and updates the checkout method accordingly.
         * @return {void}
         */
        this.usedCarService.financingType$.pipe(takeUntil(this.unsubscribe)).subscribe((type) => {
            switch (type) {
                case UsedCarFinancingTypes.Leasing:
                    this.setCheckoutMethod(UsedCarFinancingTypes.Leasing);
                    break;
                case UsedCarFinancingTypes.Cash:
                    this.setCheckoutMethod(UsedCarFinancingTypes.Cash);
                    break;
                default:
                    this.setCheckoutMethod(UsedCarFinancingTypes.Leasing);
            }
        });
    }

    financingOptions: IRadioGroupItem[] = [
        { label: 'Leasing', value: UsedCarFinancingTypes.Leasing, isChecked: true },
        { label: 'Cash', value: UsedCarFinancingTypes.Cash }
      ];

    onToggleSelect(option: string, value: UsedCarFinancingTypes) {
        this.selectedOption = option;
        this.setCheckoutMethod(value);
      }

    updateConfiguratorInputData(data: any): void {
        if (this.isInitial) {
            this.isInitial = false;
            return;
        }

        this.leasingConfigDataLoading = true;
        this.cdr.detectChanges();

        if (isNaN(data.residualValue)) {
            data.residualValue = 0;
        }

        this.cancel$.next(); // Cancel any pending "old" requests.

        this.leasingConfiguratorData.downPayment = data.downPayment;
        this.leasingConfiguratorData.monthlyTerms = data.monthlyTerms;
        this.leasingConfiguratorData.yearlyDistanceInKm = data.yearlyDistanceInKm;
        this.leasingConfiguratorData.residualValue = data.residualValue;
        this.leasingConfiguratorData.leasingType = data.leasingPlanType;
        this.leasingConfiguratorData.leasingtypeForPriceCalculation = data.leasingPurpose;

        this.getVehicleLeasingData();
    }

    validateLeasingToggles(value: string): boolean {
        if (value === undefined) {
            // If value is undefined, we return true, that is the default for fetching Motorpoint leasing data
            return true;
        }

        if (value === LEASING_PLAN_TYPES.PURPOSE_BUSINESS || value === LEASING_PLAN_TYPES.TYPE_FINANCIAL) {
            return true;
        }

        return false;
    }

    // Motorpoint leasing data logic
    getVehicleLeasingData() {
        this.leasingConfigDataLoading = true;
        this.leasingConfigDataReady = false;

        const dynamicData = {
            downpayment: this.isInitial ? undefined : this.leasingConfiguratorData?.downPayment,
            durationInMonths: this.leasingConfiguratorData?.monthlyTerms ?? 12,
            isLeasingTypeFinancial: this.validateLeasingToggles(this.leasingConfiguratorData.leasingType),
            isLeasingForBusiness: this.validateLeasingToggles(this.leasingConfiguratorData.leasingtypeForPriceCalculation),
            km: this.isInitial ? undefined : this.leasingConfiguratorData?.yearlyDistanceInKm
        };

        const reqBody = mergeObjects(this.staticRequestData, dynamicData);

        this.leasingService.getUsedCarLeasingIntegratedValues(reqBody).subscribe(
            (data: any) => {
                this.leasingPriceIsValid = data.pricePerMonth >= 1000;

                this.leasingService.broadcastUsedCarLeasingPrice(data.pricePerMonth);

                // Only update the data if the price is valid, to show correct state in slider when not
                if (this.leasingPriceIsValid) {
                    // Update leasing ranges data
                    this.leasingConfiguratorRangesData.downPaymentRange = {
                        minValue: data.downpaymentMin,
                        maxValue: data.downpaymentMax
                    };

                    // Create Months range
                    this.leasingConfiguratorRangesData.leasingMonthsRange = [];
                    for (let i = 0; i < data.leasingDurationValues.length; i++) {
                        this.leasingConfiguratorRangesData.leasingMonthsRange.push({
                            label: String(data.leasingDurationValues[i]),
                            isDefault: data.leasingDurationValues[i] === data.defaultDuration,
                            value: data.leasingDurationValues[i],
                        });
                    };

                    this.leasingConfiguratorRangesData.yearlyDistanceInKmRange = {
                        minValue: data.kmPerYearMin,
                        maxValue: data.kmPerYearMax
                    };

                    // Update Leasing slider data
                    this.leasingConfiguratorData.downPayment = data.defaultDownpayment;
                    this.leasingConfiguratorData.monthlyTerms = data.defaultDuration;
                    this.leasingConfiguratorData.yearlyDistanceInKm = data.defaultKmPerYear;
                    this.leasingConfiguratorData.monthlyPayment = data.pricePerMonth;

                    this.scrapValue = this.leasingConfiguratorData.leasingType === LEASING_PLAN_TYPES.TYPE_FINANCIAL && data.scrapValue ? data.scrapValue : undefined;
                }

                this.leasingConfigDataLoading = false;
                this.leasingConfigDataReady = true;
                this.isReady = true;
                this.cdr.markForCheck();
            },
            (error: any) => {
                console.warn('[BN Error fetching data]: Probably incorrect data');
                console.error('[BN Error fetching data]', error);
                this.leasingService.broadcastUsedCarLeasingPrice(undefined);
                this.leasingPriceIsValid = false;
                this.leasingConfigDataLoading = false;
                this.leasingConfigDataReady = true;
                this.isReady = true;
                this.cdr.markForCheck();
            }
        );
    }

    setCheckoutMethod(value: UsedCarFinancingTypes) {
        this.selectedCheckoutMethod = value;
        this.cdr.markForCheck();
    }

    goToCheckout(loanData?: LoanResponse) {
        this.selectedCheckoutMethod = UsedCarFinancingTypes.Leasing;

        // Update leasing data in checkout service
        this.checkoutService.setLeasingData(this.leasingConfiguratorData, this.leasingConfiguratorRangesData);

        this.car$.pipe(takeUntil(this.unsubscribe)).subscribe((car) => {
            const checkoutInitData = {
                vin: car?.vin?.value,
                carId: car?.id,
                loanData,
                leasingData: this.leasingConfiguratorData,
            };

            this.checkoutService.initCheckout(checkoutInitData, undefined, loanData ? 'loan' : 'cash');

            this.overlayService.open(CheckoutFlowComponent, {
                panelClass: 'full-page-takeover',
                fullHeight: true,
                data: {
                    checkoutInitData,
                    selectedCheckoutMethod: this.selectedCheckoutMethod ? this.selectedCheckoutMethod : this.usedCarFinancingTabsOrder[this.tabs.activeTab],
                    allowLeasing: this.leasingPriceIsValid
                },
            });
        });
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
